// The single source of truth for fiscal-calendar arithmetic.
//
// PURE module — no React, no zod, no router. `dataPipeline.ts` imports it; that file's only
// state-layer coupling is a TYPE-ONLY import of GranularityUnit (erased at compile time —
// see dataPipeline.ts's own import comment), so the runtime purity chain holds.
//
// BRANCH NOTE — fiscal-year constant stays independent. `release/vodafone` now has
// `~/config/tenant` (landed alongside the tenant-palette port, final-review I1), and tenant.ts
// exports its own FISCAL_YEAR_START_MONTH, but this file deliberately does NOT import it — the
// constant below is declared independently, keeping this pure fiscal-calendar module decoupled
// from config/tenant wiring. Both currently agree (April, 0-indexed); if a future sync changes
// tenant.ts's value without updating this file, isPartialFiscalYear/fiscalYearStart below keep
// using April regardless — check both when touching fiscal-year config.
//
// The two imports below (`subDays` from date-fns, `parseDateLocal` from ~/util/date) are
// load-bearing for isPartialFiscalYear below, unrelated to the tenant-constant question above.
import { subDays } from 'date-fns'
import { parseDateLocal } from '~/util/date'

// tenant.ts exists on release now (see the BRANCH NOTE above) and exports its own
// FISCAL_YEAR_START_MONTH, but this file keeps an independent declaration rather than importing
// it, matching the constant this module replaced in useTimeframePresetState.ts. Both currently
// read April (0-indexed). Typed `number`, not the literal `3`: the FY gates below compare against
// 0 and TS2367s on a non-overlapping literal.
export const FISCAL_YEAR_START_MONTH: number = 3

/** Most recent fiscal-year start on/before `date`.
 *  Zero-indexed month, local dates, no UTC math. The `>=` means the FIRST day of the start month
 *  belongs to the NEW fiscal year (April 1 2024 => FY starting April 2024; March 31 2024 => FY
 *  starting April 2023). Moved verbatim from useTimeframePresetState.ts — do not reimplement. */
export function fiscalYearStart(date: Date): Date {
  const fyYear =
    date.getMonth() >= FISCAL_YEAR_START_MONTH ? date.getFullYear() : date.getFullYear() - 1
  return new Date(fyYear, FISCAL_YEAR_START_MONTH, 1)
}

/** Start of the fiscal year AFTER the one containing `date` — the EXCLUSIVE upper bound of that
 *  fiscal year. Used by the partial-bucket predicate. */
export function nextFiscalYearStart(date: Date): Date {
  const s = fiscalYearStart(date)
  return new Date(s.getFullYear() + 1, FISCAL_YEAR_START_MONTH, 1)
}

/** FY granularity is offered only where it actually DIFFERS from the calendar year.
 *  A January fiscal year (greencrate) makes fiscalYearStart identical to startOfYear, so offering
 *  both would show two options with identical output. Semantic gate, not a tenant flag. */
export const FY_GRANULARITY_ENABLED = FISCAL_YEAR_START_MONTH !== 0

/** Label for the fiscal year STARTING at `bucketStart`, e.g. April 2024 -> "FY 24/25".
 *  Matches the olymp frame labels users already see in the Timeframe picker. A January fiscal
 *  year degenerates to the plain calendar year. */
export function fiscalYearLabel(bucketStart: Date): string {
  const y = bucketStart.getFullYear()
  if (FISCAL_YEAR_START_MONTH === 0) return String(y)
  const two = (n: number) => String(n % 100).padStart(2, '0')
  return `FY ${two(y)}/${two(y + 1)}`
}

/** True when the queried window does NOT fully cover the fiscal year CONTAINING `bucketStartIso`.
 *  Such a bucket holds a PART year while carrying a full-FY label — users read FY totals as
 *  accounting figures, so it must be marked. Both window bounds are inclusive.
 *
 *  The input is snapped to its fiscal-year start first. It used to be half-snapped: `lastDay`
 *  came via nextFiscalYearStart (which snaps internally) while the lower comparison used the RAW
 *  date, so a non-FY-start input was tested against an incoherent interval (e.g. 2024-02-01 ⇒
 *  [2024-02-01, 2024-03-31], neither a fiscal year nor the caller's bucket). Every live caller
 *  passes a summariseByUnit 'fy' key, which is already an FY start, so this changes no rendering
 *  today — it removes a latent trap. */
export function isPartialFiscalYear(
  bucketStartIso: string,
  windowFromIso: string,
  windowToIso: string,
): boolean {
  const start = fiscalYearStart(parseDateLocal(bucketStartIso))
  const lastDay = subDays(nextFiscalYearStart(start), 1)
  return parseDateLocal(windowFromIso) > start || parseDateLocal(windowToIso) < lastDay
}
